(() => {
  const esc = value => String(value ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
  const stages = ['barrel', 'points', 'flights', 'case'];
  const answerKeys = {barrel:['experience','weight','shape','position','grip','length','diameter'],points:['pointLength','pointGrip'],flights:['shaftLength','flightShape','flightDetail','system']};
  const record = value => value && typeof value === 'object' && !Array.isArray(value);
  const id = value => Number.isSafeInteger(value) && value > 0;
  const tokenLimit = 12000;
  function clean(raw) {
    if (!record(raw) || raw.v !== 1 || !record(raw.s) || !record(raw.a)) throw new Error('invalid');
    const result = {v:1,s:{},a:{},q:{},m:raw.m === 'separate' ? 'separate' : '',r:raw.r === true,g:typeof raw.g === 'string' && /^[a-zA-Z0-9-]{1,100}$/.test(raw.g) ? raw.g : ''};
    if(raw.q !== undefined && !record(raw.q)) throw new Error('invalid');
    const seen = new Set();
    for (const stage of stages) {
      if (!Object.hasOwn(raw.s, stage)) continue;
      const items = raw.s[stage];
      if (!Array.isArray(items) || items.length > (stage === 'flights' ? 3 : 1)) throw new Error('invalid');
      result.s[stage] = items.map(item => {
        if (!Array.isArray(item) || item.length !== 2 || !item.every(id) || seen.has(item[1])) throw new Error('invalid');
        seen.add(item[1]);
        const quantity=raw.q?.[String(item[1])] ?? 1;
        if(!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 9999) throw new Error('invalid');
        if(quantity > 1) result.q[String(item[1])]=quantity;
        return [item[0], item[1]];
      });
    }
    if (result.s.barrel?.length !== 1 || (Object.hasOwn(result.s, 'flights') && !Object.hasOwn(result.s, 'points'))) throw new Error('invalid');
    for (const [stage, keys] of Object.entries(answerKeys)) {
      result.a[stage] = {};
      const answers = raw.a[stage];
      if (answers !== undefined && !record(answers)) throw new Error('invalid');
      for (const key of keys) {
        if (!answers || !Object.hasOwn(answers, key)) continue;
        const values = Array.isArray(answers[key]) ? answers[key] : [answers[key]];
        if (values.length > 8 || values.some(value => typeof value !== 'string' || !/^[a-z0-9_]{1,32}$/.test(value))) throw new Error('invalid');
        result.a[stage][key] = [...new Set(values)];
      }
    }
    return result;
  }
  function encode(value) {
    const text = JSON.stringify(clean(value));
    return btoa(text).replaceAll('+','-').replaceAll('/','_').replace(/=+$/,'');
  }
  function decode(token) {
    if (typeof token !== 'string' || token.length > tokenLimit || !/^[A-Za-z0-9_-]+$/.test(token)) throw new Error('invalid');
    return clean(JSON.parse(atob(token.replaceAll('-','+').replaceAll('_','/'))));
  }
  class LFDSetupSharing {
    constructor(host) {
      this.host = host;
      this.key = 'lfd-saved-setup-v1';
      this.pending = null;
      this.remoteId = '';
      this.remoteLoading = false;
      this.remoteSequence = 0;
      this.dialogSequence = 0;
      this.entry = host.querySelector('[data-setup-saved-entry]');
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'sq-save-dialog';
      this.dialog.setAttribute('aria-label', host.t('save_title'));
      this.dialog.addEventListener('close', () => { this.dialogSequence++; this.emailForm?.destroy(); });
      host.append(this.dialog);
    }
    init() {
      this.renderEntry();
      const hash = location.hash;
      if (hash.startsWith('#lfd-id=')) {
        this.host.welcomeVisible = false;
        this.remoteId = hash.slice(8) || 'invalid';
        this.loadRemote();
        return;
      }
      if (!hash.startsWith('#lfd-setup=')) return;
      try { this.pending = decode(hash.slice(11)); this.host.welcomeVisible = false; }
      catch { this.host.welcomeVisible = false; this.host.announce('save_invalid'); }
    }
    renderEntry() {
      if (!this.entry) return;
      let exists = false;
      try { exists = !!localStorage.getItem(this.key); } catch {}
      this.entry.hidden = !exists;
      this.entry.innerHTML = exists ? `<button class="sq-link" type="button" data-action="saved-open">${esc(this.host.t('save_open_local'))}</button><button class="sq-link" type="button" data-action="saved-delete">${esc(this.host.t('save_delete_local'))}</button>` : '';
    }
    snapshot() {
      const state = this.host.state;
      const selected = Object.fromEntries(stages.filter(stage => Array.isArray(state.selected[stage])).map(stage => [stage,state.selected[stage].map(item => [item.productId,item.variantId])]));
      const flights = (selected.flights || []).map(([productId]) => this.host.products.find(product => product.id === productId));
      const quantities=Object.fromEntries((this.host.cart?.items||[]).filter(item=>item.properties?._lfd_setup_id===state.id).map(item=>[String(item.variant_id),item.quantity]));
      return clean({v:1,s:selected,a:state.answers,q:quantities,m:state.flightMode,g:state.id,r:flights.some(product => product?.profile.category === 'flight' && product.profile.requiresRing) && !flights.some(product => product?.profile.category === 'ring')});
    }
    link(snapshot) {
      const url = new URL(this.host.data.setupUrl || location.pathname, location.origin);
      if(url.origin !== location.origin) throw new Error('invalid');
      const current = new URL(location.href);
      // Shopify may remove the preview query after setting its preview cookie.
      // This optional hint only affects draft links; published links stay clean.
      const renderedTheme = window.Shopify?.theme;
      const draftId = ['unpublished', 'development', 'demo'].includes(renderedTheme?.role) ? String(renderedTheme.id || '') : '';
      const previewId = current.searchParams.get('preview_theme_id') || draftId;
      if (/^\d+$/.test(previewId)) url.searchParams.set('preview_theme_id', previewId);
      url.hash = 'lfd-setup=' + encode(snapshot);
      return url.href;
    }
    actions() {
      return this.host.state.view === 'complete' && this.host.state.selected.barrel?.length ? `<button class="sq-btn sq-complete-save" type="button" data-action="saved-show" data-complete-action="save">${esc(this.host.t('save_button_label'))}</button>` : '';
    }
    storageText(key) {
      return window.LFDSetupStorage?.text(key) || this.host.t(key === 'load_error' ? 'save_restore_error' : key === 'cancel' ? 'cancel' : 'loading');
    }
    popupText(key) {
      return window.LFDSetupStorage?.text('popup_' + key) || this.host.t('popup_' + key);
    }
    selectionMarkup() {
      const h = this.host;
      return stages.flatMap(stage => (this.dialogSnapshot.s[stage] || []).map(([productId, variantId]) => {
        const product = h.products.find(item => item.id === productId);
        const variant = product?.variants.find(item => item.id === variantId);
        const category = stage === 'flights' && ['shaft', 'flight', 'ring'].includes(product?.profile.category) ? product.profile.category : stage;
        const quantity = this.dialogSnapshot.q[variantId] || 1;
        const detail = [variant ? h.variantTitle(variant) : this.popupText('missing_variant'), quantity > 1 ? '× ' + quantity : ''].filter(Boolean).join(' · ');
        return `<div class="sq-save-product"><dt>${esc(this.popupText(category))}</dt><dd><strong>${esc(product?.title || this.popupText('missing_product'))}</strong>${detail ? `<span>${esc(detail)}</span>` : ''}</dd></div>`;
      })).join('');
    }
    async loadRemote() {
      const sequence = ++this.remoteSequence;
      this.remoteLoading = true;
      try {
        if (!window.LFDSetupStorage?.validId(this.remoteId)) throw new Error('invalid');
        const token = await window.LFDSetupStorage.load(this.remoteId);
        if (sequence !== this.remoteSequence || !this.remoteId) return;
        this.pending = decode(token);
      } catch {
        if (sequence !== this.remoteSequence) return;
        this.pending = null;
      } finally {
        if (sequence === this.remoteSequence) { this.remoteLoading = false; this.host.render(); }
      }
    }
    renderRemote() {
      const h = this.host;
      h.welcomeVisible = false;
      if (h.welcome) h.welcome.hidden = true;
      if (h.flow) h.flow.hidden = false;
      h.removeAttribute('data-welcome-active');
      for (const scene of Object.values(h.scenes || {})) { scene.stop?.(); scene.remove(); }
      h.mount.innerHTML = `<div class="sq-layout sq-saved-layout"><div class="sq-main"><h2 tabindex="-1">${esc(h.t('save_restore_title'))}</h2><p role="status">${esc(this.storageText(this.remoteLoading ? 'loading' : 'load_error'))}</p><div class="sq-save-actions">${this.remoteLoading ? '' : `<button type="button" class="sq-btn sq-btn-primary" data-action="saved-remote-retry">${esc(this.storageText('retry'))}</button>`}<button type="button" class="sq-link" data-action="saved-dismiss">${esc(this.storageText('cancel'))}</button></div></div></div>`;
      h.styleButtons();
    }
    openDialog() {
      const h = this.host;
      this.emailForm?.destroy();
      try { this.dialogSnapshot = this.snapshot(); } catch { h.announce('save_invalid'); return; }
      const link = this.link(this.dialogSnapshot);
      const subject = h.t('save_mail_subject');
      const rows = stages.flatMap(stage => (this.dialogSnapshot.s[stage] || []).map(([productId,variantId]) => {
        const p = h.products.find(product => product.id === productId);
        return p ? `${p.title} · ${h.variantTitle(p.variants.find(variant => variant.id === variantId))}${this.dialogSnapshot.q[variantId]>1?' × '+this.dialogSnapshot.q[variantId]:''}` : '';
      })).filter(Boolean).join('\n');
      const mailPrefix = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=';
      const mailBody = h.t('save_mail_body') + '\n\n' + rows + '\n\n';
      const mail = mailPrefix + encodeURIComponent(mailBody + link);
      this.dialog.dataset.saveState = 'pending';
      this.dialog.innerHTML = `<button type="button" class="sq-link sq-save-close" data-action="saved-close" aria-label="${esc(h.t('save_close'))}"><span aria-hidden="true">×</span></button><div class="sq-save-kicker"><span class="sq-save-check" aria-hidden="true">✓</span><span data-saved-kicker>${esc(this.popupText('kicker'))}</span></div><h2>${esc(h.t('save_title'))}</h2><p class="sq-save-description">${esc(h.t('save_description'))}</p><section class="sq-save-selection" aria-label="${esc(this.popupText('selection'))}"><h3>${esc(this.popupText('selection'))}</h3><dl>${this.selectionMarkup()}</dl></section><div class="sq-save-link-row"><div data-saved-id-row ${window.LFDSetupStorage?.enabled() ? '' : 'hidden'}><span class="sq-save-id-label">${esc(this.popupText('id'))}</span><strong class="sq-save-id" data-saved-id>…</strong></div><button class="sq-btn" type="button" data-action="saved-copy">${esc(h.t('save_copy'))}</button></div><div data-saved-email-slot></div><details class="sq-save-options"><summary>${esc(this.popupText('options'))}</summary><div class="sq-save-option-actions"><button class="sq-link" type="button" data-action="saved-local">${esc(h.t('save_local'))}</button><a class="sq-link" href="${esc(mail)}">${esc(h.t('save_mail'))}</a></div><p class="sq-help">${esc(h.t('save_local_note'))}</p><p class="sq-help">${esc(h.t('save_mail_note'))}</p><label class="sq-save-label">${esc(h.t('save_link_label'))}<textarea data-saved-link readonly rows="2" spellcheck="false">${esc(link)}</textarea></label>${new URL(link).searchParams.has('preview_theme_id') ? `<p class="sq-notice">${esc(h.t('save_preview_note'))}</p>` : ''}</details><p data-saved-status role="status" aria-live="polite"></p>`;
      if(!this.dialog.open) this.dialog.showModal();
      this.emailForm = window.LFDSetupEmail ? new window.LFDSetupEmail(this.dialog) : null;
      const sequence = ++this.dialogSequence;
      if (window.LFDSetupStorage?.enabled()) {
        const status = this.dialog.querySelector('[data-saved-status]');
        status.textContent = this.storageText('saving');
        window.LFDSetupStorage.save(encode(this.dialogSnapshot)).then(id => {
          if (sequence !== this.dialogSequence || !this.dialog.open) return;
          const shortLink = new URL(link);
          shortLink.hash = 'lfd-id=' + id;
          this.dialog.querySelector('[data-saved-link]').value = shortLink.href;
          this.dialog.querySelector('a[href^="mailto:"]').href = mailPrefix + encodeURIComponent(mailBody + shortLink.href);
          this.dialog.dataset.saveState = 'saved';
          this.dialog.querySelector('[data-saved-id]').textContent = id;
          this.dialog.querySelector('[data-saved-kicker]').textContent = this.popupText('saved');
          status.textContent = this.storageText('saved').replaceAll('__id__', id);
          status.classList.add('sq-save-confirmation');
          this.emailForm?.setSetupId(id);
        }).catch(() => {
          if (sequence === this.dialogSequence && this.dialog.open) {
            this.dialog.dataset.saveState = 'fallback';
            this.dialog.querySelector('[data-saved-id-row]').hidden = true;
            status.textContent = this.storageText('fallback');
          }
        });
      }
    }
    check(snapshot) {
      const h = this.host;
      const rows = stages.flatMap(stage => (snapshot.s[stage] || []).map(([productId,variantId]) => {
        const product = h.products.find(item => item.id === productId);
        const variant = product?.variants.find(item => item.id === variantId);
        return {stage,productId,variantId,product,variant,quantity:snapshot.q?.[variantId]||1,available:!!variant?.available && !variant.requiresSellingPlan};
      }));
      let valid = rows.every(row => row.available);
      const barrel = rows.find(row => row.stage === 'barrel')?.product;
      if (barrel?.profile.category !== 'barrel') valid = false;
      for (const row of rows.filter(item => item.stage === 'points')) {
        if (row.product?.profile.category !== 'points' || !window.LFDSetupEngine?.pointSystemsFit(barrel?.profile, row.product?.profile)) valid = false;
      }
      if (rows.some(row => row.stage === 'case' && row.product?.profile.category !== 'case')) valid = false;
      const flights = rows.filter(row => row.stage === 'flights').map(row => row.product);
      if (flights.length === 1) {
        if (flights[0]?.profile.category !== 'integrated' || !barrel?.profile.shaftThread || flights[0].profile.shaftThread !== barrel.profile.shaftThread) valid = false;
      } else if (flights.length > 1) {
        const shaft = flights.find(p => p?.profile.category === 'shaft');
        const flight = flights.find(p => p?.profile.category === 'flight');
        const ring = flights.find(p => p?.profile.category === 'ring');
        if (!shaft || !flight || !h.pairFits(shaft,flight) || !barrel?.profile.shaftThread || shaft.profile.shaftThread !== barrel.profile.shaftThread || flights.length !== (ring ? 3 : 2)) valid = false;
        if (ring && !h.pairFits(ring,flight)) valid = false;
        if (flight?.profile.requiresRing && !ring && !snapshot.r) valid = false;
      }
      return {rows,valid};
    }
    renderPending() {
      const h = this.host;
      h.welcomeVisible = false;
      if (h.welcome) h.welcome.hidden = true;
      if (h.flow) h.flow.hidden = false;
      h.removeAttribute('data-welcome-active');
      for (const scene of Object.values(h.scenes || {})) { scene.stop?.(); scene.remove(); }
      const {rows,valid} = this.check(this.pending);
      const items = rows.map(row => `<li>${row.product?.image ? `<img src="${esc(row.product.image)}" alt="" width="64" height="64">` : ''}<div><strong>${esc(row.product?.title || h.t('save_missing_product'))}</strong><span>${esc(row.variant ? h.variantTitle(row.variant)+(row.quantity>1?' × '+row.quantity:'') : h.t('save_missing_variant'))}</span><span>${esc(row.variant ? h.money(row.variant.price) : '')}</span>${!row.available ? `<span class="sq-diff">${esc(h.t('save_unavailable'))}</span>` : ''}</div></li>`).join('');
      const kept = ['points','flights'].filter(stage => this.pending.s[stage]?.length === 0).map(stage => `<p class="sq-help">${esc(h.t('stage_'+stage))}: ${esc(h.t('kept'))}</p>`).join('');
      h.mount.innerHTML = `<div class="sq-layout sq-saved-layout"><div class="sq-main"><p class="sq-kicker">${esc(h.t('save_kicker'))}</p><h2 tabindex="-1">${esc(h.t('save_restore_title'))}</h2><p>${esc(h.t('save_restore_intro'))}</p><ul class="sq-saved-items">${items}</ul>${kept}${this.pending.r ? `<p class="sq-help">${esc(h.t('ring_owned'))}</p>` : ''}<p class="sq-help">${esc(h.t('save_price_note'))}</p>${!valid ? `<p class="sq-notice">${esc(h.t('save_unavailable_note'))}</p>` : ''}<div class="sq-save-actions"><button type="button" class="sq-btn sq-btn-primary" data-action="saved-apply" ${valid ? '' : 'disabled'}>${esc(h.t('save_apply'))}</button><button type="button" class="sq-btn" data-action="saved-rebuild">${esc(h.t('save_rebuild'))}</button><button type="button" class="sq-link" data-action="saved-dismiss">${esc(h.t('cancel'))}</button></div></div></div>`;
      h.styleButtons();
      h.setBusy(h.busy);
    }
    clearHash() {
      this.remoteId = '';
      this.remoteLoading = false;
      this.remoteSequence++;
      if (!location.hash.startsWith('#lfd-setup=') && !location.hash.startsWith('#lfd-id=')) return;
      try { history.replaceState(history.state,'',location.pathname + location.search); } catch {}
    }
    async apply() {
      const h = this.host;
      const snapshot = this.pending;
      if (!snapshot || !this.check(snapshot).valid) { h.announce('save_unavailable_note'); return; }
      h.setBusy(true);
      h.announce('adding');
      try {
        const bytes = await crypto.subtle.digest('SHA-256',new TextEncoder().encode(encode(snapshot)));
        const digestId = 'saved-' + Array.from(new Uint8Array(bytes)).map(byte => byte.toString(16).padStart(2,'0')).join('');
        let cart = await h.api('cart.js');
        const rows = this.check(snapshot).rows;
        const matches = item => rows.some(row => item.properties?._lfd_stage === row.stage && String(item.variant_id) === String(row.variantId) && item.quantity === row.quantity);
        const candidates = [...new Set([snapshot.g,this.importId,digestId,...cart.items.map(item => item.properties?._lfd_setup_id)].filter(Boolean))];
        let setupId = candidates.find(candidate => {
          const group = cart.items.filter(item => item.properties?._lfd_setup_id === candidate);
          return group.length > 0 && group.every(matches) && (candidate === snapshot.g || candidate === this.importId || candidate === digestId || group.length === rows.length);
        });
        if (!setupId) setupId = cart.items.some(item => item.properties?._lfd_setup_id === digestId) ? crypto.randomUUID() : digestId;
        this.importId = setupId;
        const own = row => cart.items.some(item => item.properties?._lfd_setup_id === setupId && item.properties?._lfd_stage === row.stage && String(item.variant_id) === String(row.variantId) && item.quantity === row.quantity);
        const missing = rows.filter(row => !own(row));
        if (missing.length) await h.api('cart/add.js',{items:missing.map(row => ({id:row.variantId,quantity:row.quantity,properties:{_lfd_setup_id:setupId,_lfd_stage:row.stage}}))});
        cart = await h.api('cart.js');
        if (!rows.every(own)) throw new Error('cart');
        h.cart = cart;
        h.state = {id:setupId,stage:Object.hasOwn(snapshot.s,'points') ? 2 : 1,step:0,view:Object.hasOwn(snapshot.s,'flights') ? 'complete' : 'question',answers:JSON.parse(JSON.stringify(snapshot.a)),selected:Object.fromEntries(stages.filter(stage => Object.hasOwn(snapshot.s,stage)).map(stage => [stage,snapshot.s[stage].map(([productId,variantId]) => ({productId,variantId}))]))};
        if (snapshot.m) h.state.flightMode = snapshot.m;
        h.clearPair();
        h.normalizeAnswers();
        this.pending = null;
        this.clearHash();
        h.render();
        h.updateCartCount();
        h.announce('save_restored');
        h.focusMain();
      } catch {
        h.announce('save_restore_error');
      } finally { h.setBusy(false); }
    }
    handle(action, button) {
      if (!action.startsWith('saved-')) return false;
      const h = this.host;
      if (action === 'saved-show') this.openDialog();
      if (action === 'saved-remote-retry' && !this.remoteLoading) { this.loadRemote(); h.render(); }
      if (action === 'saved-close') { this.dialogSequence++; this.dialog.close(); }
      if (action === 'saved-copy') {
        const input = this.dialog.querySelector('[data-saved-link]');
        const status = this.dialog.querySelector('[data-saved-status]');
        status.classList.remove('sq-save-confirmation');
        const manual = () => {
          this.dialog.querySelector('.sq-save-options').open = true;
          input.focus(); input.select();
          status.textContent = h.t('save_copy_manual');
        };
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(input.value).then(() => { status.textContent = h.t('save_copied'); }).catch(manual);
        else manual();
      }
      if (action === 'saved-local') {
        const status = this.dialog.querySelector('[data-saved-status]');
        status.classList.remove('sq-save-confirmation');
        try { localStorage.setItem(this.key,encode(this.dialogSnapshot)); status.textContent = h.t('save_stored'); this.renderEntry(); }
        catch { status.textContent = h.t('save_storage_error'); }
      }
      if (action === 'saved-open') {
        this.clearHash();
        try { this.pending = decode(localStorage.getItem(this.key)); h.welcomeVisible = false; h.render(); h.focusMain(); }
        catch { h.welcomeVisible = false; h.render(); h.announce('save_invalid'); }
      }
      if (action === 'saved-delete') {
        try { localStorage.removeItem(this.key); this.renderEntry(); }
        catch { h.welcomeVisible = false; h.render(); h.announce('save_storage_error'); }
      }
      if (action === 'saved-apply') this.apply();
      if (action === 'saved-dismiss') { this.pending = null; this.clearHash(); h.render(); h.focusMain(); }
      if (action === 'saved-rebuild') {
        const answers = this.pending?.a;
        if (answers) {
          h.state = {id:crypto.randomUUID(),stage:0,step:0,view:'question',answers:JSON.parse(JSON.stringify(answers)),selected:{}};
          h.clearPair(); h.normalizeAnswers();
          this.pending = null; this.clearHash(); h.render(); h.focusMain();
        }
      }
      return true;
    }
  }
  window.LFDSetupSharing = LFDSetupSharing;
})();

